// The parts of InfoTip that aren't React: where the panel goes, and when
// it goes away. Kept apart so they can be tested without a browser.

export interface Box {
  top: number;
  left: number;
  bottom: number;
}

export interface PanelSize {
  width: number;
  height: number;
}

export interface ViewportSize {
  width: number;
  height: number;
}

/** Closest the panel gets to the viewport's edge, and to its icon. */
export const VIEWPORT_GUTTER = 16;
export const ANCHOR_GAP = 6;

/** Below the icon if it fits, else above if that has more room; always
 *  clamped so a panel near an edge, or taller than the screen, stays on it. */
export function placeInfoTip(anchor: Box, panel: PanelSize, viewport: ViewportSize): { top: number; left: number } {
  const maxLeft = viewport.width - panel.width - VIEWPORT_GUTTER;
  const left = Math.max(VIEWPORT_GUTTER, Math.min(anchor.left, maxLeft));

  const below = anchor.bottom + ANCHOR_GAP;
  const above = anchor.top - panel.height - ANCHOR_GAP;
  const fitsBelow = below + panel.height <= viewport.height - VIEWPORT_GUTTER;
  const roomAbove = anchor.top - VIEWPORT_GUTTER;
  const roomBelow = viewport.height - VIEWPORT_GUTTER - anchor.bottom;
  const preferred = fitsBelow || roomBelow >= roomAbove ? below : above;

  const maxTop = viewport.height - panel.height - VIEWPORT_GUTTER;
  const top = Math.max(VIEWPORT_GUTTER, Math.min(preferred, maxTop));
  return { top, left };
}

type WindowEvents = Pick<Window, 'addEventListener' | 'removeEventListener'>;

export interface DismissHandlers {
  /** True when an event's target is the icon or the panel. */
  isInside: (target: EventTarget | null) => boolean;
  /** Escape was pressed. */
  onEscape: () => void;
  /** A press landed outside the icon and the panel. */
  onOutsidePress: () => void;
  /** That press has finished, so whatever it did to focus has happened. */
  onOutsidePressSettled: () => void;
  /** The page moved under the panel. */
  onReposition: (target: EventTarget | null) => void;
}

/** Wires up the ways an open panel closes or moves, and returns the undo.
 *
 *  Everything listens on `window` in the capture phase. The Modal closes
 *  on an Escape keydown reaching `window`, so catching Escape first and
 *  stopping it means Escape closes the panel and leaves the modal open.
 *  Capture also means a press still counts when the element under it
 *  stops propagation. */
export function bindInfoTipDismiss(win: WindowEvents, handlers: DismissHandlers): () => void {
  const onKeyDown = (event: Event) => {
    if ((event as KeyboardEvent).key !== 'Escape') return;
    event.stopPropagation();
    handlers.onEscape();
  };

  const onPointerDown = (event: Event) => {
    if (handlers.isInside(event.target)) return;
    handlers.onOutsidePress();
    const settle = () => {
      win.removeEventListener('pointerup', settle);
      win.removeEventListener('pointercancel', settle);
      setTimeout(handlers.onOutsidePressSettled, 0);
    };
    win.addEventListener('pointerup', settle);
    win.addEventListener('pointercancel', settle);
  };

  const onMoved = (event: Event) => handlers.onReposition(event.target);

  win.addEventListener('keydown', onKeyDown, true);
  win.addEventListener('pointerdown', onPointerDown, true);
  win.addEventListener('scroll', onMoved, true);
  win.addEventListener('resize', onMoved);
  return () => {
    win.removeEventListener('keydown', onKeyDown, true);
    win.removeEventListener('pointerdown', onPointerDown, true);
    win.removeEventListener('scroll', onMoved, true);
    win.removeEventListener('resize', onMoved);
  };
}
